import * as Dialog from '@radix-ui/react-dialog'
import css from './views.module.css'
import { useMemo } from 'react'
import { useEventTypes, useRunEvents } from '../data/queries'
import { EMPTY_REGISTRY, renderEvent, resolve, toRegistry } from '../data/map/eventTypes'
import { clock, shortDate } from '../lib/time'
import { usd } from '../lib/money'
import { at, str, num, obj } from '../lib/json'
import type { QueueItem } from '../data/map/approvals'
import { Button } from '../ui'

/**
 * The run trace: every event the runner wrote for this run, oldest first.
 *
 * Radix Dialog is the one UI dependency in the project, used unstyled and only
 * here and for the agent card. It brings the focus trap, the scroll lock and
 * the aria wiring, which are the parts of a drawer that are genuinely hard and
 * that the keyboard model would otherwise fight.
 */
export function RunTrace({ item, onClose }: { item: QueueItem | null; onClose: () => void }) {
  const events = useRunEvents(item?.runId ?? null)
  // Each step in the registry's words. The run's own fields are not to hand
  // here, so a template that needs them falls back to "{label}: {summary}" --
  // which, inside a drawer already titled with the run, is the better line.
  const types = useEventTypes()
  const registry = useMemo(() => (types.data ? toRegistry(types.data) : EMPTY_REGISTRY), [types.data])

  return (
    <Dialog.Root open={item !== null} onOpenChange={(open) => { if (!open) onClose() }}>
      <Dialog.Portal>
        <Dialog.Overlay className={css.scrim} />
        <Dialog.Content className={css.drawer} aria-describedby={undefined}>
          {item ? (
            <>
              <div className={css.drawerHead}>
                <div>
                  <div className="microLabel">{item.department} · {item.source}</div>
                  <Dialog.Title className={css.drawerTitle}>{item.title}</Dialog.Title>
                </div>
                <Dialog.Close asChild><Button variant="quiet">Close</Button></Dialog.Close>
              </div>

              <div className={css.provGrid}>
                <Provenance label="Repo" value={item.repo} />
                <Provenance label="Branch" value={item.buildBranch} />
                <Provenance label="Verdict" value={item.verdict} />
                <Provenance label="Attempt" value={item.attempt === null ? null : String(item.attempt)} />
              </div>

              {events.isLoading ? (
                <p className={css.stepDetail} style={{ marginTop: 14 }}>Reading the run…</p>
              ) : events.data && events.data.length > 0 ? (
                <div style={{ marginTop: 6 }}>
                  {events.data.map((event) => {
                    // Cost is on the event that carries it and nowhere else; a
                    // per-step figure is not invented for steps without one.
                    const cost = num(at(event.payload, 'cost_usd')) ?? num(at(event.payload, 'result', 'total_cost_usd'))
                    const res = resolve(registry, event.type)
                    const def = res.kind === 'event' ? res.def : null
                    return (
                      <div key={event.id} className={css.step}>
                        <span className={`${css.stepTime} mono`} title={shortDate(event.ts)}>{clock(event.ts)}</span>
                        <span>
                          <span className={css.stepLabel}>{def ? renderEvent(def, event) : event.summary ?? event.type}</span>
                          <span className={`${css.stepDetail} mono`} style={{ display: 'block' }}>{detailFor(event.payload) ?? event.type}</span>
                        </span>
                        <span className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                          {cost === null ? '' : usd(cost, { precise: true })}
                        </span>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <p className={css.stepDetail} style={{ marginTop: 14 }}>
                  No events are linked to this run.
                </p>
              )}

              {item.state !== 'pending' ? (
                <div style={{ marginTop: 18, borderTop: '1px solid var(--line)', paddingTop: 14 }}>
                  <div className="microLabel">Decision record</div>
                  <p className={css.stepLabel} style={{ marginTop: 6 }}>
                    {item.state} · {item.decidedBy ?? 'unknown'}
                    {item.decisionNote ? ` · ${item.decisionNote}` : ''}
                  </p>
                  <p className={`${css.stepDetail} mono`}>
                    approval {item.id} · run {item.runId ?? '—'}
                  </p>
                  <p className={css.stepDetail}>
                    The record is the appended event, not this row. Immutable, and kept for seven
                    years alongside the run.
                  </p>
                </div>
              ) : null}
            </>
          ) : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

function Provenance({ label, value }: { label: string; value: string | null }) {
  if (!value) return null
  return (
    <div>
      <div className="microLabel">{label}</div>
      <div className="mono" style={{ fontSize: 12, marginTop: 3, overflowWrap: 'anywhere' }}>{value}</div>
    </div>
  )
}

/** A short, readable line out of whatever the runner put in the payload. */
function detailFor(payload: unknown): string | null {
  const o = obj(payload as never)
  if (!o) return null
  const candidates = ['branch', 'pr_url', 'sha', 'model', 'repo', 'state', 'verdict']
  const parts = candidates.map((key) => (str(o[key]) ? `${key} ${str(o[key])}` : null)).filter(Boolean)
  return parts.length > 0 ? parts.join(' · ') : null
}
